// src/pages/ReceptionistDashboard.js
import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import demoImage from "../../assets/images/patientbed.png";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Dropdown,
} from "react-bootstrap";
import axios from "axios";
import toast from "react-hot-toast";
import ReAppointmentModal from "../../components/ReAppointmentModal";
import LoginHistory from "../../components/LoginHistory";
import * as Yup from "yup";
import { FaCalendarAlt, FaUsers, FaClipboardList } from "react-icons/fa";


const BASE_URL = process.env.REACT_APP_BASE_URL;

// ✅ reusable string validator
const requiredTrimmedString = (msg) =>
  Yup.string()
    .required(msg)
    .test(
      "no-spaces-only",
      `${msg} cannot be empty or just spaces`,
      (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
    );

// ✅ reusable validators
const preventSpaces = (e) => {
  if (
    e.key === " " &&
    (!e.target.value || /^\s*$/.test(e.target.value + e.key))
  ) {
    e.preventDefault();
  }
};
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData("text"))) {
    e.preventDefault();
  }
};

const ReceptionistDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem("token");

  const [hospitalDetails, setHospitalDetails] = useState({
    hospital_id: "",
    subhospital_id: "",
  });
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [showReAppointmentModal, setShowReAppointmentModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [stats, setStats] = useState({
    todayAppointments: 0,
    pendingApprovals: 0,
    totalPatients: 0,
  });
  const intervalRef = useRef(null);
  

  useEffect(() => {
    // Add an extra entry to browser history
    window.history.pushState(null, "", window.location.href);

    const handleBack = () => {
      window.history.go(1); // prevent back
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, []);

  // ✅ Validation Schema for Re-appointment Modal
  const validationSchema = Yup.object({
    doctorId: Yup.string().required("Select a doctor"),
    appointmentDate: Yup.string().required("Select a date"),
    appointmentTime: Yup.string().required("Select a time"),
    reason: requiredTrimmedString("Reason"),
    additionalPhone: Yup.string()
      .optional()
      .matches(/^\d{10}$/, "Additional phone number must be exactly 10 digits"),
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const authUser =JSON.parse(localStorage.getItem("user"));
    const receptionist_id =localStorage.getItem("receptionist_id");


    if (!token || !authUser) return;

    const { hospital_id, sub_hospital_id } = authUser;
    setHospitalDetails({
      hospital_id,
      subhospital_id: sub_hospital_id,
    });

    // Fetch doctors
    axios
      .get(`${BASE_URL}/api/patientAppointment/doctors`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hospital_id, subhospital_id: sub_hospital_id,receptionist_id },
        receptionist_id
      })
      .then((res) => setDoctors(res.data))
      .catch(() => toast.error("Failed to load doctors."));
  }, []);

  // ✅ Fetch Dashboard Data
  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(
        localStorage.getItem("authUser") || localStorage.getItem("user")
      );
      if (!token || !user) return;

      const { hospital_id, sub_hospital_id } = user;

      // Today’s Appointments
      const resToday = await axios.get(
        `${BASE_URL}/api/patientAppointment/todays-appointments`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { hospital_id, subhospital_id: sub_hospital_id },
        }
      );
      setTodayAppointments(resToday.data || []);
      setStats((prev) => ({
        ...prev,
        todayAppointments: (resToday.data || []).length,
      }));

      // Pending Approvals
      const resPending = await axios.get(
        `${BASE_URL}/api/patientAppointment/pending-appointments`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { hospital_id, subhospital_id: sub_hospital_id },
        }
      );
      setStats((prev) => ({
        ...prev,
        pendingApprovals: (resPending.data || []).length,
      }));

      // Total Patients
      const resAll = await axios.get(
        `${BASE_URL}/api/patientAppointment/appointments`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { hospital_id, subhospital_id: sub_hospital_id },
        }
      );
      setStats((prev) => ({
        ...prev,
        totalPatients: new Set((resAll.data || []).map((a) => a.patientName))
          .size,
      }));
    } catch {
      toast.error("Failed to load dashboard data.");
    }
  };

  useEffect(() => {
    fetchDashboardData();
    intervalRef.current = setInterval(fetchDashboardData, 10000);
    return () => clearInterval(intervalRef.current);
  }, []);

  // ✅ Handle status change
  const handleStatusUpdate = async (appointmentId, newStatus) => {
    let dbStatus =
      newStatus === "confirmed"
        ? "Confirmed"
        : newStatus === "disapproved"
        ? "Cancel"
        : "Reschedule";

    try {
      await axios.put(
        `${BASE_URL}/api/patientAppointment/update-status/${appointmentId}`,
        { status: dbStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Appointment ${dbStatus}`);
      fetchDashboardData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  // ✅ Re-appointment
  const handleReAppointmentClick = async (appointment) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/patientAppointment/get-patient-id`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { patientName: appointment.patientName },
        }
      );
      const patientId = response.data.patient_id;
      if (!patientId) {
        toast.error("Patient ID not found.");
        return;
      }
      setSelectedPatient({
        id: patientId,
        patientName: appointment.patientName,
        patientPhone: appointment.patientPhone,
        hospital_id: appointment.hospital_id,
        subhospital_id: appointment.subhospital_id,
      });
      setShowReAppointmentModal(true);
    } catch {
      toast.error("Failed to fetch patient ID.");
    }
  };

  // ✅ Doctor/date change
  const handleDoctorChange = (doctorId, setFieldValue) => {
    setFieldValue("doctorId", doctorId);
    setFieldValue("appointmentDate", "");
    setFieldValue("appointmentTime", "");
    setAvailableTimes([]);
  };
  const handleDateChange = async (date, doctorId, setFieldValue) => {
    setFieldValue("appointmentDate", date);
    setFieldValue("appointmentTime", "");
    if (doctorId && date) {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/patientAppointment/doctor-availability`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              doctor_id: doctorId,
              date,
              hospital_id: hospitalDetails.hospital_id,
              subhospital_id: hospitalDetails.subhospital_id,
            },
          }
        );
        setAvailableTimes(response.data);
      } catch {
        toast.error("Error fetching available times");
      }
    }
  };

  // ✅ Submit re-appointment
  const handleReAppointmentSubmit = async (values, { resetForm }) => {
    if (!selectedPatient || !selectedPatient.id) {
      toast.error("Patient details missing.");
      return;
    }
    const [start_time, end_time] = values.appointmentTime
      .split(" -")
      .map((t) => t.trim());
    const payload = {
      patient_id: selectedPatient.id,
      doctor_id: values.doctorId,
      appointment_date: values.appointmentDate,
      start_time: `${values.appointmentDate} ${start_time}:00`,
      end_time: `${values.appointmentDate} ${end_time}:00`,
      reason: values.reason,
      hospital_id: selectedPatient.hospital_id,
      subhospital_id: selectedPatient.subhospital_id,
    };
    try {
      await axios.post(
        `${BASE_URL}/api/patientAppointment/create-appointment`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Re-Appointment booked!");
      setShowReAppointmentModal(false);
      fetchDashboardData();
      resetForm();
    } catch {
      toast.error("Error booking re-appointment");
    }
  };

  return (
    <Container fluid className="p-4">
      {/* Header */}
      <Card className="shadow-sm p-3 mb-4">
        <Row className="align-items-center">
          <Col md={8}>
            <h6 className="text-muted">Good Morning,</h6>
            <h2 className="fw-bold text-teal primary-class">
              Welcome, {user?.name || "Receptionist"}
            </h2>
            <p className="text-muted mb-0">
              Your health, your records, your appointments – all in one place.
            </p>
          </Col>
          <Col md={4} className="text-center">
            <img
              src={demoImage}
              alt="Dashboard"
              style={{ maxHeight: "120px" }}
            />
          </Col>
        </Row>
      </Card>

      {/* Stats */}
      <Row className="mb-4 g-3">
        <Col md={4}>
          <Card className="stat-card shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="vertical-line me-3"></div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="stat-count">{stats.todayAppointments}</span>
                  <FaCalendarAlt className="stat-icon" />
                </div>
                <h6 className="mb-0 fw-bold mt-3">Today’s Appointments</h6>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stat-card shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="vertical-line me-3"></div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="stat-count">{stats.pendingApprovals}</span>
                  <FaClipboardList className="stat-icon" />
                </div>
                <h6 className="mb-0 fw-bold mt-3">Pending Approvals</h6>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stat-card shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="vertical-line me-3"></div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <span className="stat-count">{stats.totalPatients}</span>
                  <FaUsers className="stat-icon" />
                </div>
                <h6 className="mb-0 fw-bold mt-3">Total Patients</h6>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Active Time */}
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <h6 className="fw-bold mb-3">Your Today’s Active Time</h6>
          <LoginHistory userId={user?.id} />
        </Card.Body>
      </Card>

      {/* Today’s Appointments */}
      <h5 className="fw-bold mb-3">Today’s Appointments</h5>
      <Card className="shadow-sm mb-4">
        <Table responsive hover>
          <thead className="table-light">
            <tr>
              <th>S.No</th>
              <th>Patient</th>
              <th>Doctor</th>
              <th>Time</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {todayAppointments.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-danger">
                  No appointments today.
                </td>
              </tr>
            ) : (
              todayAppointments.map((a, idx) => (
                <tr key={a.appointmentId || a.id || idx}>
                  <td>{idx + 1}</td>
                  <td>{a.patientName}</td>
                  <td>{a.doctorName}</td>
                  <td>{a.startTime || "--"}</td>
                  <td>
                    <Badge
                      bg={
                        a.status === "Pending"
                          ? "warning"
                          : a.status === "Confirmed"
                          ? "success"
                          : a.status === "Reschedule"
                          ? "info"
                          : "secondary"
                      }
                    >
                      {a.status}
                    </Badge>
                  </td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle
                        id={`action-${idx}`}
                        className="btn btn-sm custom-btn"
                      >
                        Action
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() =>
                            handleStatusUpdate(a.appointmentId, "confirmed")
                          }
                          disabled={a.status === "Confirmed"}
                        >
                          Approve
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() =>
                            handleStatusUpdate(a.appointmentId, "disapproved")
                          }
                          disabled={a.status === "Cancel"}
                        >
                          Cancel
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleReAppointmentClick(a)}
                        >
                          Reschedule
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      {/* Re-Appointment Modal */}
      <ReAppointmentModal
        show={showReAppointmentModal}
        onHide={() => setShowReAppointmentModal(false)}
        selectedPatient={selectedPatient}
        doctors={doctors}
        availableTimes={availableTimes}
        validationSchema={validationSchema}
        handleDoctorChange={handleDoctorChange}
        handleDateChange={handleDateChange}
        preventSpaces={preventSpaces}
        preventPasteSpaces={preventPasteSpaces}
        onSubmit={handleReAppointmentSubmit}
      />
    </Container>
  );
};

export default ReceptionistDashboard;
